import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRangeValue, TAdditionalServicesValue, TRoomsValue } from '../../types/quiz';
import {
  EFormStatus,
  EFormStep,
  IRequestFormV2,
  IRequestFormV2Errors,
  IRegion,
} from '../../types/requestForm/requestFormV2';

export const initialRequestFormState: IRequestFormV2 = {
  step: EFormStep.Rooms,
  status: EFormStatus.Interactive,
  meta: {
    showRequestForm: false,
    requiresAuth: false,
  },
  quiz: {
    roomCount: null,
    priceRange: null,
    additionalServices: null,
  },
  form: {
    phone: null,
    requirements: null,
    region: null,
  },
  phoneVerification: {
    code: null,
    secondsToRetry: 0,
    lastAttemptTimestamp: null,
  },
  errors: {
    phone: null,
    phoneVerification: null,
  },
};

export const requestFormV2Slice = createSlice({
  name: 'requestFormV2',
  initialState: initialRequestFormState,
  extraReducers: form => {
    form
      .addCase('consultingRequest/createConsultingRequest/pending', state => {
        state.status = EFormStatus.Submitting;
        state.errors.phoneVerification = null;
        state.phoneVerification.lastAttemptTimestamp = null;
      })
      .addCase('consultingRequest/createConsultingRequest/fulfilled', state => {
        state.status = EFormStatus.Interactive;
        state.phoneVerification.secondsToRetry = 60;
        state.phoneVerification.lastAttemptTimestamp = new Date().getTime();
      })
      .addCase('consultingRequest/createConsultingRequest/rejected', state => {
        state.status = EFormStatus.Interactive;
        state.step = EFormStep.Error;
        state.phoneVerification.secondsToRetry = 0;
        state.phoneVerification.lastAttemptTimestamp = null;
      });

    form
      .addCase('phoneVerification/requestPhoneVerificationCode/pending', state => {
        state.status = EFormStatus.Submitting;
        state.errors.phoneVerification = null;
        state.phoneVerification.code = null;
        state.phoneVerification.lastAttemptTimestamp = null;
      })
      .addCase('phoneVerification/requestPhoneVerificationCode/fulfilled', state => {
        state.status = EFormStatus.Interactive;
        state.phoneVerification.secondsToRetry = 60;
        state.phoneVerification.lastAttemptTimestamp = new Date().getTime();
      })
      .addCase('phoneVerification/requestPhoneVerificationCode/rejected', state => {
        state.status = EFormStatus.Interactive;
        state.phoneVerification.secondsToRetry = 0;
        state.phoneVerification.code = null;
        state.phoneVerification.lastAttemptTimestamp = null;
      });

    form
      .addCase('requestButton/canUserCreateRequest/pending', state => {
        state.status = EFormStatus.Submitting;
      })
      .addCase('requestButton/canUserCreateRequest/fulfilled', state => {
        state.status = EFormStatus.Interactive;
      })
      .addCase('requestButton/canUserCreateRequest/rejected', state => {
        state.status = EFormStatus.Interactive;
      });
  },
  reducers: {
    setFormStep: (state, action: PayloadAction<EFormStep>) => {
      state.step = action.payload;
    },
    setRoomCount: (state, action: PayloadAction<TRoomsValue[] | null>) => {
      state.quiz.roomCount = action.payload;
    },
    setPriceRange: (state, action: PayloadAction<IRangeValue>) => {
      state.quiz.priceRange = action.payload;
    },
    setAdditionalServices: (state, action: PayloadAction<TAdditionalServicesValue>) => {
      state.quiz.additionalServices = action.payload;
    },
    setRegion: (state, action: PayloadAction<IRegion | null>) => {
      state.form.region = action.payload;
    },
    setFormPhone: (state, action: PayloadAction<string>) => {
      state.form.phone = action.payload;
    },
    setFormRequirements: (state, action: PayloadAction<string>) => {
      state.form.requirements = action.payload;
    },
    setPhoneVerificationCode: (state, action: PayloadAction<string>) => {
      state.phoneVerification.code = action.payload;
    },
    setErrors: (state, action: PayloadAction<IRequestFormV2Errors>) => {
      state.errors = action.payload;
    },
    clearErrors: state => {
      state.errors = {
        phone: null,
        phoneVerification: null,
      };
    },
    setConformationCode: (state, action: PayloadAction<string>) => {
      state.phoneVerification.code = action.payload;
    },
    clearConformationCode: state => {
      state.phoneVerification.code = null;
    },
    resetForm: state => {
      state.step = EFormStep.Rooms;
      state.quiz.roomCount = null;
      state.quiz.priceRange = null;
      state.quiz.additionalServices = null;
    },
  },
});

export const {
  reducer: requestFormV2Reducer,
  actions: {
    setFormStep,
    setRoomCount,
    setPriceRange,
    setAdditionalServices,
    resetForm,
    setRegion,
    setFormPhone,
    setFormRequirements,
    setPhoneVerificationCode,
    setErrors,
    setConformationCode,
    clearErrors,
    clearConformationCode,
  },
} = requestFormV2Slice;
